import SockJS from "sockjs-client";
import Stomp from "stompjs";
import { DEV_APP_ENV, DEV_MODE, ENCRYPT_PHARAGRAPH } from "../config/const";
import { IClientInfo } from "../store/models/FingerPrintModel";
import { decodeToken, getUrlWebSocketByRole } from "../utils/general/utils";
import { Roles } from "../utils/types/const";
import { useRef, useState } from "react";

const useClientWebSocket = (setIsLoading: any) => {
  // dependencies
  var aesEcb = require("aes-ecb");
  // local states
  const [showModalAlertDevice, setShowModalAlertDevice] =
    useState<boolean>(false);
  const [responseSocketTemp, setResponseSocketTemp] = useState<IClientInfo>({
    dateTime: "",
    httpStatus: 0,
    ip: "",
    mac: "",
    deviceSerial: "",
    message: "",
    responseCode: null,
  });

  const stompClient = useRef<any>(null);
  const socketWS = useRef<any>(null);
  const hasRequestedConfig = useRef<boolean>(false);
  var urlSocket = getUrlWebSocketByRole();

  /**
   * Decrypt data
   * @function
   * @param {any} data data to submit
   */
  const decryptDataReceibed = (data: any) => {
    if (!data || typeof data !== "string") return data;

    // Si ya es una dirección MAC o IP válida en texto plano, no desencriptar
    const isAlreadyPlainMac = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(data.trim());
    const isAlreadyPlainIp = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(data.trim());
    if (isAlreadyPlainMac || isAlreadyPlainIp) {
      return data.trim();
    }

    try {
      const dataDecrypted = aesEcb.decrypt(ENCRYPT_PHARAGRAPH, data);
      // El front nunca debe devolver el valor cifrado recibido del desktop:
      // se retorna siempre el resultado del desencriptado, aunque no sea ASCII limpio,
      // para que el consumidor (formulario/API) reciba la información descifrada.
      if (typeof dataDecrypted === "string") {
        return dataDecrypted.replace(/[\0-\x1F]/g, "").trim();
      }
      return dataDecrypted;
    } catch (error) {
      console.error("Error al desencriptar dato recibido del desktop:", error);
      return "";
    }
  };

  // Parsing and Encrypt data to send
  function parseObjectData(obj: any) {
    try {
      const objParsed = JSON.parse(obj);
      return objParsed;
    } catch (error) { }
  }

  // Flujo Clásico (Roles ADMIN, RNEC, etc.): conexión al servidor remoto sin checkConfig
  const openSocketConnection = (
    urlSocket: string,
    _customFunction?: any,
    urlLocal?: any,
    onExhausted?: () => void
  ) => {
    if (stompClient.current?.connected) return;

    // stompjs puede invocar el errorCallback dos veces para un mismo intento
    // fallido (frame STOMP ERROR + ws.onclose). Este flag es local a esta
    // invocación y evita disparar el fallback más de una vez por intento.
    let hasHandledError = false;

    let socket = new SockJS(`${urlSocket}clientInfo`);
    stompClient.current = Stomp.over(socket);

    stompClient.current.connect(
      {},
      (frame: any) => {
        // Recordar la url base que realmente respondió, para que useFingerWebSocket
        // conecte la captura de huella al mismo destino (evita que asuma 8080/finger.gse
        // por defecto cuando el clientInfo terminó conectando por otra url).
        sessionStorage.setItem("socket-connected-url", urlSocket);

        stompClient.current.subscribe("/topic/clientInfo", (message: any) => {
          const parsedData = parseObjectData(message.body);

          !urlLocal
            ? callbackSuscribe(parsedData, _customFunction, true)
            : callbackSuscribe(parsedData, _customFunction);
        });

        stompClient.current.send(
          "/app/clientInfo",
          {},
          JSON.stringify({ dev: DEV_MODE })
        );
      },
      (error: any) => {
        if (hasHandledError) return;
        hasHandledError = true;

        if (!!urlLocal) {
          openSocketConnection(urlLocal, _customFunction, null, onExhausted);
        } else if (onExhausted) {
          onExhausted();
        } else {
          console.error("Error : ", error);
          setShowModalAlertDevice(true);
        }
      }
    );
  };

  // Paso 3 de la cadena capturador (BIOMETRIA_CAPTURADOR, BIOMETRIA_CAPTURADOR_MOVIL):
  // se invoca cuando fallan finger.gse y 127.0.0.1:8080. Validación de configuración
  // por empresa vía /app/checkConfig en 127.0.0.1:8087.
  const openSocketConnectionCapturador = (
    urlSocket: string,
    _customFunction?: any,
    urlLocal?: any
  ) => {
    if (stompClient.current?.connected) return;

    // Mismo motivo que en openSocketConnection: evitar doble ejecución del
    // fallback si stompjs invoca el errorCallback más de una vez por intento.
    let hasHandledError = false;

    let socket = new SockJS(`${urlSocket}clientInfo`);
    stompClient.current = Stomp.over(socket);

    stompClient.current.connect(
      {},
      (frame: any) => {
        // Recordar la url base que realmente respondió (ver openSocketConnection)
        sessionStorage.setItem("socket-connected-url", urlSocket);

        // Suscribirse antes de enviar para escuchar respuestas de configuración y cliente
        stompClient.current.subscribe("/topic/clientInfo", (message: any) => {
          const parsedData = parseObjectData(message.body);

          !urlLocal
            ? callbackSuscribe(parsedData, _customFunction, true)
            : callbackSuscribe(parsedData, _customFunction);
        });

        stompClient.current.subscribe("/topic/checkConfig", (message: any) => {
          const parsedData = parseObjectData(message.body);

          !urlLocal
            ? callbackSuscribe(parsedData, _customFunction, true)
            : callbackSuscribe(parsedData, _customFunction);
        });

        // Llamado 1: Consulta inicial de validación del estado del desktop
        sendRequestClientInfo();
      },
      (error: any) => {
        if (hasHandledError) return;
        hasHandledError = true;

        if (!!urlLocal) {
          openSocketConnectionCapturador(urlLocal, _customFunction, null);
        } else {
          console.error("Error : ", error);
          setShowModalAlertDevice(true);
        }
      }
    );
  };

  // Open and handle socket connection for different roles
  const connectClientInfo = (_customFunction?: any, handleLoading?: any) => {
    if (socketWS.current?.readyState === WebSocket.OPEN) return;

    const role = urlSocket?.role;
    const isCapturador =
      role === Roles.BIOMETRIA_CAPTURADOR ||
      role === Roles.BIOMETRIA_CAPTURADOR_MOVIL;

    if (role === "movil") {
      socketWS.current = new WebSocket(`${urlSocket.url}clientInfo`);

      socketWS.current.onopen = () => {
        socketWS.current.send("");
      };

      socketWS.current.onerror = () => {
        setShowModalAlertDevice(true);
      };

      socketWS.current.onmessage = (_response: { data: any }) => {
        const parsedObj = parseObjectData(_response.data);

        sessionStorage.setItem("pos_latitud", parsedObj?.latitude || "");
        sessionStorage.setItem("pos_longitud", parsedObj?.longitude || "");

        callbackSuscribe(parsedObj, _customFunction);
      };
    } else if (isCapturador) {
      urlSocket &&
        openSocketConnection(
          urlSocket?.url,
          _customFunction,
          urlSocket?.urlLocal,
          () =>
            openSocketConnectionCapturador(
              urlSocket?.urlCheckConfig || "",
              _customFunction,
              null
            )
        );
    } else {
      urlSocket &&
        openSocketConnection(
          urlSocket?.url,
          _customFunction,
          urlSocket?.urlLocal
        );
    }
  };

  function disconnectClientInfo() {
    if (stompClient.current != null) {
      stompClient.current.disconnect();
      stompClient.current = null;
    } else if (socketWS.current !== null) {
      socketWS.current.close();
      socketWS.current = null;
    }
    hasRequestedConfig.current = false;
    setIsLoading(false);
  }

  // Llamado 2: Enviar orden de configuración con el JWT token cuando el desktop no esté configurado
  const sendConfigurationToDesktop = () => {
    const token = sessionStorage.getItem("jwtToken") || "";

    const configPayload: any = {
      env: DEV_APP_ENV,
      dev: DEV_MODE,
      token: token,
    };

    if (stompClient.current?.connected) {
      stompClient.current.send(
        "/app/clientInfo",
        {},
        // JSON.stringify(configPayload)
        JSON.stringify({ dev: DEV_MODE })
      );
    }
  };

  const callbackSuscribe = (
    _data: any,
    _customFunction?: any,
    isEncrypted: boolean = false
  ) => {
    const shouldEncrypt =
      Boolean(isEncrypted) ||
      (typeof _data?.mac === "string" && _data.mac.length > 20 && _data.mac.endsWith("=")) ||
      (typeof _data?.ip === "string" && _data.ip.length > 15 && _data.ip.endsWith("="));

    if (shouldEncrypt) {
      sessionStorage.setItem("aes-encrypt", "1");
    } else {
      sessionStorage.setItem("aes-encrypt", "0");
    }

    if (_data) {
      // Validar si el desktop indica que requiere configuración o no está configurado
      const needsConfig =
        _data.isConfigured === false ||
        _data.needsConfiguration === true ||
        _data.responseCode === "NEED_CONFIG" ||
        _data.responseCode === "NOT_CONFIGURED" ||
        _data.httpStatus === 412;

      if (needsConfig && !hasRequestedConfig.current) {
        hasRequestedConfig.current = true;
        sendConfigurationToDesktop();
        return;
      }

      // Si es respuesta de checkConfig (R10) sin datos de lector, solicitar clientInfo para obtener serial y hardware
      const isCheckConfigResponse =
        _data.responseCode === "R10" ||
        (!_data.deviceSerial && !_data.serial);

      if (isCheckConfigResponse && !hasRequestedConfig.current) {
        hasRequestedConfig.current = true;
        sendConfigurationToDesktop();
        return;
      }

      const rawMac = _data.mac || _data.MAC;
      const rawSerial = _data.serial || _data.deviceSerial || _data.Serial;

      // El desktop siempre cifra ip/mac/deviceSerial en este endpoint, sin excepción,
      // así que el front siempre debe intentar desencriptar en vez de confiar en la
      // heurística `shouldEncrypt` (que puede fallar, p.ej. en el flujo "movil").
      // decryptDataReceibed ya detecta si el valor llega en texto plano y lo respeta.
      let objToSend = {
        dateTime: _data.dateTime,
        httpStatus: _data.httpStatus,
        ip: _data.ip ? decryptDataReceibed(_data.ip) : _data.ip,
        mac: rawMac ? decryptDataReceibed(rawMac) : rawMac,
        deviceSerial: rawSerial ? decryptDataReceibed(rawSerial) : "",
        message: _data.message,
        responseCode: _data.responseCode,
      };

      setResponseSocketTemp(objToSend);
      disconnectClientInfo();

      if ((!_data.responseCode || _data.responseCode === "R10") && _customFunction) {
        _customFunction(objToSend);
      }
    }
  };

  // Llamado 1: Petición de consulta de validación inicial al desktop enviando el token JWT
  const sendRequestClientInfo = () => {
    const token = sessionStorage.getItem("jwtToken") || "";

    const payload: any = {
      env: DEV_APP_ENV,
      dev: DEV_MODE,
      token: token,
    };

    if (stompClient.current?.connected) {
      stompClient.current.send(
        "/app/checkConfig",
        {},
        JSON.stringify(payload)
      );
    }
  };

  return {
    showModalAlertDevice,
    setShowModalAlertDevice,
    responseSocketTemp,
    connectClientInfo,
    disconnectClientInfo,
  };
};

export default useClientWebSocket;
